import {
  convert,
  DayOfWeek,
  LocalDate,
  LocalDateTime,
  TemporalAdjusters,
  ZonedDateTime,
  ZoneId,
} from "@js-joda/core";
import { useState } from "react";
import DropOffLocation from "@modules/drop-off-location/types/DropOffLocation";
import { Trans, useTranslation } from "next-i18next";
import useUpdatePurchaseDropOffLocationAppointmentDateMutation from "@modules/purchase/hooks/useUpdatePurchaseDropOffLocationAppointmentDateMutation";
import { Purchase } from "@modules/purchase/types/Purchase";
import { useRouter } from "next/router";
import { formatTimeOnly } from "@talladega/helpers";
import WeeklyAppointmentTimePicker from "@components/WeeklyAppointmentTimePicker";
import {
  Button,
  LoadingSpinner,
  Modal,
  OverlayLoader,
  toastPop,
} from "@talladega/components";
import useUnavailableHoursForPeriod from "@modules/drop-off-location/hooks/getUnavailableHoursForWeek";
import "@js-joda/timezone/dist/js-joda-timezone-10-year-range";

type Props = {
  purchase: Purchase;
  dropOffLocation: DropOffLocation | undefined;
  onAppointmentConfirmed: () => void;
  isLoading: boolean;
};

const PickDropOffAppointmentDate = ({
  dropOffLocation,
  purchase,
  onAppointmentConfirmed,
  isLoading,
}: Props) => {
  const { t } = useTranslation(["common", "purchase"]);
  const { locale } = useRouter();
  const [isSaving, setIsSaving] = useState(false);
  const [selectedAppointmentDateTime, setSelectedAppointmentDateTime] =
    useState<LocalDateTime | null>(null);
  const [
    isAppointmentConfirmationModalOpen,
    setIsAppointmentConfirmationModalOpen,
  ] = useState<boolean>(false);

  const updatePurchaseDropOffLocationAppointmentDateMutation =
    useUpdatePurchaseDropOffLocationAppointmentDateMutation();

  const onConfirmDateAndTime = () => {
    setIsSaving(true);

    if (selectedAppointmentDateTime && dropOffLocation) {
      updatePurchaseDropOffLocationAppointmentDateMutation.mutate(
        {
          dealId: purchase.id,
          appointmentAt: selectedAppointmentDateTime,
          dropOffLocationId: dropOffLocation.id,
          locale: locale || "en",
        },
        {
          onSettled: () => {
            setIsSaving(false);
          },
          onSuccess: (response) => {
            if (response.isAppointmentUnavailable) {
              toastPop(
                "warning",
                t("purchase:appointment_unavailable"),
                t("purchase:sorry_appointment_unavailable")
              );
            } else {
              onAppointmentConfirmed();
            }
            setIsAppointmentConfirmationModalOpen(false);
          },
        }
      );
    }
  };

  const onAppointmentDateTimeSelected = (
    appointmentDateTime: LocalDateTime | null
  ) => {
    setSelectedAppointmentDateTime(appointmentDateTime);
  };

  const [showWeekOf, setShowWeekOf] = useState(
    LocalDate.now().with(TemporalAdjusters.previousOrSame(DayOfWeek.SUNDAY))
  );

  const { unavailableSlots } = useUnavailableHoursForPeriod(
    dropOffLocation?.id,
    LocalDate.now().plusDays(1),
    LocalDate.now()
      .plusWeeks(2)
      .with(TemporalAdjusters.nextOrSame(DayOfWeek.SATURDAY))
  );

  const handleWeekBack = () => {
    const previousWeek = showWeekOf.with(
      TemporalAdjusters.previous(DayOfWeek.SUNDAY)
    );

    // don't let them go earlier than "today", or the week containing "today"
    if (
      !previousWeek.isBefore(
        LocalDate.now().with(TemporalAdjusters.previousOrSame(DayOfWeek.SUNDAY))
      )
    ) {
      setShowWeekOf(previousWeek);
    }
  };

  const handleWeekForward = () => {
    // still wanna make sure they can't go too far ahead
    const maxDate = LocalDate.now()
      .plusWeeks(2)
      .with(TemporalAdjusters.nextOrSame(DayOfWeek.SATURDAY));
    const nextWeek = showWeekOf.with(TemporalAdjusters.next(DayOfWeek.SUNDAY));

    if (maxDate.isAfter(nextWeek)) {
      setShowWeekOf(nextWeek);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <p className="text-lg font-normal text-secondary">
        {t("purchase:please_select_a_drop_off_date_message")}
      </p>
      <LoadingSpinner isLoading={isLoading} isOverlay />
      {!isLoading &&
        !isSaving &&
        dropOffLocation?.regularHours &&
        dropOffLocation?.specialHours && (
          <div className="px-4 py-6 shadow-elevation-01 rounded-lg">
            <WeeklyAppointmentTimePicker
              regularHours={dropOffLocation.regularHours}
              specialHours={dropOffLocation.specialHours}
              onAppointmentDateTimeSelected={onAppointmentDateTimeSelected}
              minAppointmentDate={ZonedDateTime.now(
                ZoneId.of(dropOffLocation.timeZone)
              )
                .toLocalDate()
                .plusDays(1)}
              weekOfDate={showWeekOf}
              existingAppointmentDateTime={
                purchase.dropOffDetails?.dropOffLocationAppointmentAt
                  ? LocalDateTime.parse(
                      purchase.dropOffDetails?.dropOffLocationAppointmentAt
                    )
                  : null
              }
              onWeekBack={handleWeekBack}
              onWeekForward={handleWeekForward}
              unavailableTimeSlots={unavailableSlots}
              timeZone={dropOffLocation.timeZone}
            />
          </div>
        )}

      <div>
        <Button
          size="large"
          className="flex justify-center items-center"
          buttonStyle="primary"
          fill="outline"
          disabled={!selectedAppointmentDateTime}
          onClick={() => setIsAppointmentConfirmationModalOpen(true)}
        >
          <p>{t("common:confirm_date_and_time")}</p>
        </Button>
      </div>
      <Modal
        title={t("common:summary")}
        isOpen={isAppointmentConfirmationModalOpen}
        modalType="none"
        onClose={() => setIsAppointmentConfirmationModalOpen(false)}
        alignment="center"
        disableClosing={isLoading || isSaving}
      >
        <OverlayLoader isLoading={isLoading || isSaving}>
          <>
            <h5>{t("purchase:ready_to_book_an_appointment")}</h5>
            <Trans
              t={t}
              i18nKey="purchase:appointment_confirmation_message"
              values={{
                vehicleYear: purchase?.tradeIns[0].year,
                vehicleMake: purchase?.tradeIns[0].make,
                vehicleModel: purchase?.tradeIns[0].model,
                locationName: dropOffLocation?.name,
                appointmentDate: selectedAppointmentDateTime
                  ? convert(selectedAppointmentDateTime)
                      .toDate()
                      .toLocaleString(locale, {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                  : " ",
                appointmentTime: selectedAppointmentDateTime
                  ? formatTimeOnly(
                      convert(selectedAppointmentDateTime).toDate(),
                      `${locale === "fr" ? "fr" : "en"}-CA`
                    )
                  : " ",
              }}
              components={{
                b: <span className="font-semibold" />,
              }}
            />
          </>
          <Modal.Buttons
            primaryButtonText={t("purchase:lets_do_it")}
            secondaryButtonText={t("common:cancel")}
            onPrimaryClick={onConfirmDateAndTime}
            onSecondaryClick={() =>
              setIsAppointmentConfirmationModalOpen(false)
            }
          />
        </OverlayLoader>
      </Modal>
    </div>
  );
};

export default PickDropOffAppointmentDate;
